import { useCallback, useMemo } from 'react';
import { NudosSearchbarDropdown } from '../../../../NudosComponents';
import { IOrganizationConfig } from '../../../../../types/product.configuration.types';

import './SavedConfigClassSelector.scss';

interface SavedConfigClassSelectorProps {
  selectedConfigClassName: string;
  currentSavedConfigClasses: IOrganizationConfig[];
  selectedSavedClassName: string;
  onSavedClassSelection: (configName: string, isNew: boolean) => void;
}

const SavedConfigClassSelector = ({
  selectedConfigClassName,
  currentSavedConfigClasses,
  selectedSavedClassName,
  onSavedClassSelection
}: SavedConfigClassSelectorProps) => {
  const currentSavedConfigNames = useMemo(() => {
    return currentSavedConfigClasses.filter(({ name }) => !!name).map(({ name }) => name!);
  }, [currentSavedConfigClasses]);

  const formDropdownOptionsCallback = useCallback(
    (options: string[], clickOptionCallback?: (option: string) => void, searchString?: string) => {
      if (currentSavedConfigNames.length === 0 && !searchString) {
        return [
          <div
            key="no-configs-message"
            className="optionContainer savedCavedConfigClassSelectionOpcisabled text-center"
          >
            <span>
              No hay configuraciones guardadas para <strong>{selectedConfigClassName}</strong>. Crea una nueva
              escribiendo el nombre de la configuración.
            </span>
          </div>
        ];
      }

      const hasPartialMatch = searchString && options.some(o => o.toLowerCase().includes(searchString.toLowerCase()));

      if (searchString && !hasPartialMatch) {
        return [
          <div
            key={searchString}
            className="optionContainer savedConfigClassSelectionOption"
            onClick={() => {
              onSavedClassSelection(searchString, true);
              clickOptionCallback?.(searchString);
            }}
          >
            <span>{searchString}</span>
            <strong>-</strong>
            <strong>Nuevo</strong>
          </div>
        ];
      }

      return options.map(option => {
        return (
          <div
            key={option}
            className="optionContainer savedConfigClassSelectionOption"
            onClick={() => {
              onSavedClassSelection(option, false);
              clickOptionCallback?.(option);
            }}
          >
            <span>{option}</span>
          </div>
        );
      });
    },
    [currentSavedConfigNames, selectedConfigClassName, onSavedClassSelection]
  );

  const formSelectedOptionUICallback = useCallback((selectedOption: string) => {
    return <span className="savedConfigClassSelectionSelectedOption">{selectedOption}</span>;
  }, []);

  const filterBySearchStringCallback = useCallback((search: string, options: string[]) => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(option => option.toLowerCase().includes(searchLower));
  }, []);

  return (
    <div className="savedConfigClassSelectorContainer">
      <label className="savedConfigClassSelectorLabel">Configuraciones guardadas</label>
      <NudosSearchbarDropdown
        customClassname="w-full"
        rawOptions={currentSavedConfigNames}
        formDropdownOptionsCallback={formDropdownOptionsCallback}
        formSelectedOptionUICallback={formSelectedOptionUICallback}
        filterBySearchStringCallback={filterBySearchStringCallback}
        customPlaceholder="Selecciona o crea una configuración"
        currentValue={selectedSavedClassName || undefined}
        isFilled={!!selectedSavedClassName}
        includeMagnifierIcon={false}
      />
    </div>
  );
};

export default SavedConfigClassSelector;
